import React from 'react'
import Loading from '../layout/Loading'
import useScreenSize from '../../../hocs/useScreenSize'

export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode,
  variant?: 'primary' | 'secondary' | 'tertiary' | 'flat' | 'feedback' | 'premium',
  size?: 'sm' | 'md' | 'lg',
  iconOnlyBelow?: 'sm' | 'md' | 'lg',
  iconOnlyAbove?: 'sm' | 'md' | 'lg',
  outlined?: boolean,
  icon?: React.ReactElement
  loading?: boolean
  disabled?: boolean
  className?: string
}
const Button: React.FC<Props> = ({ children, icon, variant = 'primary', size = 'md', outlined, loading, disabled, className, iconOnlyBelow, iconOnlyAbove, ...rest }) => {
  const screenSize = useScreenSize()
  let variantTheme = ''
  let svgTheme = ''
  if(variant === 'primary' && outlined) {
    variantTheme = 'bg-transparent border border-2 border-deep-green text-deep-green justify-center'
    svgTheme = 'stroke-deep-green'
  } else if(variant === 'primary' && !outlined) {
    variantTheme = 'bg-deep-green text-white justify-center'
    svgTheme = 'stroke-white'
  } else if(variant === 'secondary' && outlined) {
    variantTheme = 'bg-transparent border border-1 border-deep-green text-deep-green justify-center'
    svgTheme = 'stroke-electric-green'
  } else if(variant === 'secondary' && !outlined) {
    variantTheme = 'bg-electric-green text-white'
    svgTheme = 'stroke-white'
  } else if(variant === 'flat') {
    variantTheme = 'text-black font-weight-600'
    svgTheme = 'stroke-deep-green'
  } else if (variant === 'tertiary') {
    variantTheme = 'bg-deep-green-200 text-deep-green'
    svgTheme = 'stroke-inherit'
  } else if (variant === 'feedback') {
    variantTheme = 'bg-black hover:bg-black/90 hover:-translate-y-0.5 hover:shadow-lg text-white'
    svgTheme = 'stroke-white'
  } else if (variant === 'premium') {
    variantTheme = 'bg-orange hover:shadow-sm text-white'
  }

  let sizeClass = 'min-w-32'
  if(size === 'sm') {
    sizeClass = `min-w-5 rounded-full  py-1 sm:py-2 text-2xs sm:text-sm ${variant === 'flat' ? 'px-0' : 'px-2 sm:px-3'}`
    svgTheme = `${svgTheme} w-4 h-4`
  } else if(size === 'md') {
    sizeClass = `h-[50px] min-w-32 rounded-full py-2 sm:py-3 text-xs sm:text-base ${variant === 'flat' ? 'px-0' : 'px-3 sm:px-5'}`
    svgTheme = `${svgTheme} w-5 h-5`
  } else if(size === 'lg') {
    sizeClass = 'sm:min-w-48 rounded-full px-3 sm:px-5 py-2 sm:py-3 text-md sm:text-xl'
    svgTheme = `${svgTheme} h-6 w-6`
  }

  if(!children || (iconOnlyBelow && screenSize.isSmallerOrEqual(iconOnlyBelow)) || (iconOnlyAbove && screenSize.isBiggerOrEqual(iconOnlyAbove))) {
    sizeClass = 'p-3 rounded-full'
  }

  const ButtonLabel = iconOnlyBelow && screenSize.isSmallerOrEqual(iconOnlyBelow)
    ? <div className={`hidden ${iconOnlyBelow}:block`}>{children}</div>
    : children

  return (
    <button
      className={`disabled:opacity-60 inline-flex items-center justify-start gap-x-2 ${sizeClass} text-center tracking-wide font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${variantTheme} ${className}`}
      disabled={loading || disabled}
      {...rest}
    >
      {!loading && icon && React.cloneElement(icon as React.ReactElement, { className: `${svgTheme} ${icon.props?.className}`, width: 25, height: 25, stroke: 'currentColor' })}
      {loading && <Loading />}
      {
        (iconOnlyAbove && screenSize.isSmallerOrEqual(iconOnlyAbove) ) ||
        (iconOnlyBelow && screenSize.isBiggerOrEqual(iconOnlyBelow)) ||
        (!iconOnlyAbove && !iconOnlyBelow)
        ? ButtonLabel
        : null
      }
    </button>
  )
}

export default Button
